import React, { useState, useCallback } from "react";
import { ToastNotification, ToastContainer } from "@/components/ui/Toast";
import { ToastContext, ToastContextType } from "./ToastContextTypes";

// Maximum number of toasts to show at once
const MAX_TOASTS = 5;

interface ToastProviderProps {
  children: React.ReactNode;
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  maxToasts = MAX_TOASTS,
}) => {
  const [notifications, setNotifications] = useState<ToastNotification[]>([]);

  // Generate unique ID for toast
  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Add a new toast notification
  const addToast = useCallback(
    (toast: Omit<ToastNotification, "id">) => {
      const id = generateId();
      const newToast: ToastNotification = {
        ...toast,
        id,
      };

      setNotifications((prev) => {
        // Add new toast and limit to maxToasts
        const updated = [newToast, ...prev];
        return updated.slice(0, maxToasts);
      });

      return id;
    },
    [generateId, maxToasts],
  );

  // Remove a specific toast
  const removeToast = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Clear all toasts
  const clearAllToasts = useCallback(() => {
    setNotifications([]);
  }, []);

  const contextValue: ToastContextType = {
    notifications,
    addToast,
    removeToast,
    clearAllToasts,
  };
  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer
        notifications={notifications}
        position="top-end"
        onDismiss={removeToast}
      />
    </ToastContext.Provider>
  );
};
